// LivestreamOverlayCta.jsx
import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import LivestreamOverlayFooter from '../container/LivestreamOverlayFooter.js';
import WithLivestreamRating from '../container/WithLivestreamRating.js';
import DecryptionWrapper from '../container/DecryptionWrapper.js';

import AudioLiveCover from '../component/AudioLiveCover.jsx';
import Rating from '../component/Rating.jsx';
import LivestreamOverlaySwiper from '../component/LivestreamOverlaySwiper.jsx';
import { SwiperSlide } from '../component/Swiper.jsx';
import LivestreamOverlayChatroom from '../container/LivestreamOverlayChatroom.js';

import { TranslationNamespace } from '../resource/translationNamespace.js';
import { EMPTY_ARRAY, NULL_FUNCTION } from '../resource/defaults.js';
import { ModalId, ButtonId } from '../resource/trackingConstants.js';
import {
  LivestreamMode,
  ASIDE_WIDTH,
} from '../resource/liveStreamConstants.js';
import getTranslationKey from '../resource/getTranslationKey.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import getPublicSrcSet from '../resource/getPublicSrcSet.js';
import { getUserPicture, MediaAssetFormat } from '../resource/getMediaAsset.js';

import ResetButtonStyle from '../style/ResetButtonStyle.js';
import { color, breakpoint } from '../style/variables.js';
import media from '../style/media.js';

import PurchaseFailedImgSrc from '../../img/img-purchase-failed.png';

export const LivestreamOverlayCta = ({
  streamId = null,
  meId = null,
  username = '',
  avatarColor = '',
  streamTitle = '',
  categories = EMPTY_ARRAY,
  balance = 0,
  mode = LivestreamMode.FREE,
  exclusivePrice = null,
  addvalue = NULL_FUNCTION,
  unlockPrivateStream = NULL_FUNCTION,
  goBack = NULL_FUNCTION,
  setModalTracking = NULL_FUNCTION,
  sessionId = '',
  isLiveStreamSnapshotEnabled = false,
  publicUrlHostname = '',
  isExclusivePrivatePerforming = false,
  isExclusivePrivatePreparing = false,
  isMeExclusivePrivateInviteInitiator = false,
  isFetchingStreamToken = false,
  isResolvingStreamToken = false,
  isAsideExpanded = true,
  deviceWidth = 0,
  isAudioLive = false,
}) => {
  const { t } = useTranslation();
  const isInsufficientDiamondForExclusivePrivate = balance < exclusivePrice;
  const mixpanelModalId =
    LivestreamMode.ONE_VS_ONE === mode
      ? isInsufficientDiamondForExclusivePrivate
        ? ModalId.LiveStream.ModalExclusivePrivatePurchaseDiamond
        : ModalId.LiveStream.ModalDeductionFailed
      : LivestreamMode.SHOW === mode
        ? ModalId.LiveStream.LivestreamShowPurchaseModal
        : LivestreamMode.ONE_VS_N === mode
          ? ModalId.LiveStream.PrivateLivestreamDetail
          : undefined;
  const isExclusivePrivateOngoing =
    isExclusivePrivatePerforming || isExclusivePrivatePreparing;
  // While an exclusive private is ongoing
  // hide the session snapshot to avoid leaking its content — unless I initiated it.
  const shouldShowSnapshot =
    !!sessionId &&
    isLiveStreamSnapshotEnabled &&
    (!isExclusivePrivateOngoing || isMeExclusivePrivateInviteInitiator);
  const snapshotSource = useMemo(() => {
    if (!shouldShowSnapshot) return null;
    return getResourceUrl({
      endpoint: `/sessions/${sessionId}/snapshot?hostname=${publicUrlHostname}`,
    }).href;
  }, [publicUrlHostname, sessionId, shouldShowSnapshot]);
  const userPictureSource = useMemo(() => {
    if (!streamId) return null;
    return getUserPicture({
      userId: streamId,
      meId,
      size: 512,
      format: MediaAssetFormat.JPG,
    }).href;
  }, [meId, streamId]);
  const coverSource = shouldShowSnapshot ? snapshotSource : userPictureSource;

  // Set modal tracking
  useEffect(() => {
    setModalTracking({
      modalId: mixpanelModalId,
    });
  }, [mixpanelModalId, setModalTracking]);

  const renderContent = () => {
    if (isResolvingStreamToken) return null;

    // Only appears once the token fetch has settled with no valid token.
    switch (mode) {
      case LivestreamMode.ONE_VS_ONE: {
        if (isInsufficientDiamondForExclusivePrivate) {
          return (
            <StyledLivestreamOverlayCta>
              <InsufficientBalanceSection $isAsideExpanded={isAsideExpanded}>
                <InsufficientBalanceCard>
                  <ExclusiveTextGroup>
                    <ExclusiveTitle>
                      {t('alert_insufficient_funds', {
                        ns: TranslationNamespace.NOTIFICATION,
                      })}
                    </ExclusiveTitle>
                    <ExclusiveBody>
                      {t('stream_exclusive_waiting', {
                        ns: TranslationNamespace.LIVESTREAM,
                      })}
                    </ExclusiveBody>
                    <ExclusiveHint>
                      {t('stream_exclusive_waiting_description', {
                        ns: TranslationNamespace.LIVESTREAM,
                      })}
                    </ExclusiveHint>
                  </ExclusiveTextGroup>
                  <ActionButtons>
                    <TopUpButton
                      type="button"
                      data-element_id={
                        ButtonId.LiveStream.LivestreamShowPurchaseModal
                      }
                      data-tracking_payload={{ modalId: mixpanelModalId }}
                      disabled={isFetchingStreamToken}
                      onClick={() => {
                        if (isFetchingStreamToken) return;
                        return addvalue();
                      }}
                    >
                      {t('button_buy_now', {
                        ns: TranslationNamespace.LIVESTREAM,
                      })}
                    </TopUpButton>
                    <TextButton
                      type="button"
                      data-element_id={ButtonId.All.ButtonLeave}
                      onClick={goBack}
                    >
                      {t('button_leave_exclusive', {
                        ns: TranslationNamespace.LIVESTREAM,
                      })}
                    </TextButton>
                  </ActionButtons>
                </InsufficientBalanceCard>
              </InsufficientBalanceSection>
            </StyledLivestreamOverlayCta>
          );
        }

        return (
          <StyledLivestreamOverlayCta>
            <Overlay>
              <ModalWrapper>
                <PaymentFailedImage>
                  <img src={PurchaseFailedImgSrc} alt="" />
                </PaymentFailedImage>
                <ExclusiveTextGroup>
                  <ExclusiveTitle>
                    {t('title_deduction_failed', {
                      ns: TranslationNamespace.LIVESTREAM,
                    })}
                  </ExclusiveTitle>
                  <ExclusiveBody>
                    {t('deduction_failed_exclusive_description', {
                      ns: TranslationNamespace.LIVESTREAM,
                    })}
                  </ExclusiveBody>
                </ExclusiveTextGroup>
                <ActionButtons>
                  <RetryButton
                    type="button"
                    data-element_id={ButtonId.All.ButtonRetry}
                    data-tracking_payload={{ modalId: mixpanelModalId }}
                    disabled={isFetchingStreamToken}
                    onClick={() => {
                      if (isFetchingStreamToken) return;
                      return unlockPrivateStream({ streamId });
                    }}
                  >
                    {t('button_retry', { ns: TranslationNamespace.LIVESTREAM })}
                  </RetryButton>
                  <TextButton
                    $muted
                    type="button"
                    data-element_id={ButtonId.All.ButtonClose}
                    onClick={goBack}
                  >
                    {t('button_leave_livestream', {
                      ns: TranslationNamespace.LIVESTREAM,
                    })}
                  </TextButton>
                </ActionButtons>
              </ModalWrapper>
            </Overlay>
          </StyledLivestreamOverlayCta>
        );
      }
      case LivestreamMode.SHOW:
      case LivestreamMode.ONE_VS_N: {
        const isPrivateShow = LivestreamMode.SHOW === mode;

        const renderFooterContent = () => (
          <LivestreamOverlayFooter
            streamId={streamId}
            isPrivateShow={isPrivateShow}
            mixpanelModalId={mixpanelModalId}
            shouldShowCountdownWhenPerforming={isPrivateShow}
          />
        );

        const renderLivestreamOverlayInfo = () => (
          <Body>
            <Header>
              {streamTitle ||
                t('title_in_private_stream', {
                  ns: TranslationNamespace.LIVESTREAM,
                })}
            </Header>
            {categories.length > 0 && (
              <CategoriesWrapper>
                {categories.map(category => (
                  <CategoryItem key={category}>
                    {t(
                      getTranslationKey({
                        key: `livestream_settings_tags_${category}`,
                      }),
                      {
                        ns: TranslationNamespace.LIVESTREAM,
                      }
                    )}
                  </CategoryItem>
                ))}
              </CategoriesWrapper>
            )}
            <WithLivestreamRating id={streamId}>
              {({
                ratingStepSize,
                displayRating,
                displayRatingCount,
                rating,
                isDisplayRatingEnabled,
              }) =>
                !!rating && isDisplayRatingEnabled ? (
                  <RatingSummary>
                    <DisplayRating>{displayRating}</DisplayRating>
                    <Rating
                      value={displayRating || 0}
                      max={100 / ratingStepSize}
                      iconSize={20}
                      isReadOnly
                    />
                    <DisplayRatingCount>
                      {displayRatingCount}
                    </DisplayRatingCount>
                  </RatingSummary>
                ) : null
              }
            </WithLivestreamRating>
            <Footer>{renderFooterContent()}</Footer>
            {isAudioLive ? (
              <AudioLiveCoverLayer>
                <AudioLiveCover
                  userId={streamId}
                  meId={meId}
                  username={username}
                  avatarColor={avatarColor}
                />
              </AudioLiveCoverLayer>
            ) : (
              <SnapshotPicture>
                {coverSource && (
                  <DecryptionWrapper resourceUrl={coverSource}>
                    {[MediaAssetFormat.AVIF, MediaAssetFormat.WEBP].map(
                      format => (
                        <source
                          key={format}
                          type={`image/${format}`}
                          srcSet={
                            getPublicSrcSet({
                              href: coverSource,
                              size: 512,
                              format,
                            }).srcSet
                          }
                        />
                      )
                    )}
                    <img
                      {...getPublicSrcSet({
                        href: coverSource,
                        size: 512,
                        format: MediaAssetFormat.JPG,
                      })}
                      alt={`${username}'s livestream cover`}
                    />
                  </DecryptionWrapper>
                )}
              </SnapshotPicture>
            )}
          </Body>
        );

        const renderLivestreamOverlayChatroom = () => {
          if (deviceWidth > breakpoint.tablet) return null;
          return (
            <LivestreamOverlayChatroom
              streamId={streamId}
              footerContent={renderFooterContent()}
            />
          );
        };

        const slides = [
          {
            key: 'overlay-info',
            content: renderLivestreamOverlayInfo(),
          },
          {
            key: 'chatroom',
            content: renderLivestreamOverlayChatroom(),
          },
        ].filter(item => item.content != null);

        return (
          <StyledLivestreamOverlayCta>
            <CenteredStreamSection $isAsideExpanded={isAsideExpanded}>
              <OverlayPanel>
                <LivestreamOverlaySwiper>
                  {slides.map(({ key, content }) => (
                    <SwiperSlide key={key}>{content}</SwiperSlide>
                  ))}
                </LivestreamOverlaySwiper>
              </OverlayPanel>
            </CenteredStreamSection>
          </StyledLivestreamOverlayCta>
        );
      }
      default:
        return null;
    }
  };

  return renderContent();
};

LivestreamOverlayCta.propTypes = {
  streamId: PropTypes.string,
  meId: PropTypes.string,
  username: PropTypes.string,
  avatarColor: PropTypes.string,
  mode: PropTypes.string,
  streamTitle: PropTypes.string,
  categories: PropTypes.arrayOf(PropTypes.string),
  balance: PropTypes.number,
  exclusivePrice: PropTypes.number,
  addvalue: PropTypes.func,
  unlockPrivateStream: PropTypes.func,
  goBack: PropTypes.func,
  setModalTracking: PropTypes.func,
  sessionId: PropTypes.string,
  isLiveStreamSnapshotEnabled: PropTypes.bool,
  publicUrlHostname: PropTypes.string,
  isExclusivePrivatePerforming: PropTypes.bool,
  isExclusivePrivatePreparing: PropTypes.bool,
  isMeExclusivePrivateInviteInitiator: PropTypes.bool,
  isFetchingStreamToken: PropTypes.bool,
  isResolvingStreamToken: PropTypes.bool,
  isAsideExpanded: PropTypes.bool,
  deviceWidth: PropTypes.number,
  isAudioLive: PropTypes.bool,
};

const StyledLivestreamOverlayCta = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

const StreamSection = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: calc(
    100% - ${({ $isAsideExpanded }) => ($isAsideExpanded ? ASIDE_WIDTH : 0)}px
  );
  ${media.tablet`
    width: 100%;
  `}
`;

const CenteredStreamSection = styled(StreamSection)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const InsufficientBalanceSection = styled(StreamSection)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const InsufficientBalanceCard = styled.div`
  width: 300px;
  max-width: 100%;
  padding-bottom: 160px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  pointer-events: auto;
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: ${color.neutral['0_alpha_80']};
  pointer-events: auto;
`;

const ModalWrapper = styled.div`
  width: 300px;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  padding: 24px 24px 16px;
  border-radius: 8px;
  background: ${color.neutral[10]};
  box-shadow: 0px 4px 16px 2px ${color.neutral['0_alpha_20']};
`;

const PaymentFailedImage = styled.div`
  width: 128px;
  height: 128px;
  border-radius: 8px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    user-select: none;
    pointer-events: none;
  }
`;

const ExclusiveTextGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const ExclusiveTitle = styled.div`
  color: ${color.neutral[100]};
  text-align: center;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.5;
  letter-spacing: 0.02px;
`;

const ExclusiveBody = styled.div`
  color: ${color.neutral[100]};
  text-align: center;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0.014px;
`;

const ExclusiveHint = styled.div`
  width: 100%;
  color: ${color.neutral['100_alpha_80']};
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0.012px;
`;

const ActionButtons = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const FilledButton = styled.button`
  ${ResetButtonStyle};
  display: flex;
  width: 100%;
  height: 32px;
  padding: 2px 8px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  color: ${color.neutral[0]};
  font-size: 16px;
  font-weight: 600;
  line-height: 1.5;
  letter-spacing: 0.016px;

  :disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const TopUpButton = styled(FilledButton)`
  background: ${color.tealBlue};
`;

const RetryButton = styled(FilledButton)`
  background: ${color.neutral[100]};
`;

const TextButton = styled.button`
  ${ResetButtonStyle};
  display: flex;
  height: 28px;
  padding: 2px 8px;
  justify-content: center;
  align-items: center;
  color: ${({ $muted }) => ($muted ? color.neutral[30] : color.neutral[100])};
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5;
  letter-spacing: 0.014px;
`;

const OverlayPanel = styled.div`
  width: 443.25px;
  overflow: hidden;
  pointer-events: auto;
  ${media.tablet`
    width: 100vw;
  `}
`;

const AudioLiveCoverLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
`;

const SnapshotPicture = styled.picture`
  position: absolute;
  z-index: -1;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  background-color: ${color.black};

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    user-select: none;
    pointer-events: none;
  }
  ::after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0.3) 0%,
      rgba(0, 0, 0, 0.05) 16.68%,
      rgba(0, 0, 0, 0.3) 52.99%,
      rgba(0, 0, 0, 0.6) 100%
    );
  }
`;

const Header = styled.div`
  min-height: 27px;
  display: flex;
  align-items: left;
  justify-content: left;
  font-size: 18px;
  line-height: 1.5;
  font-weight: 600;
  color: ${color.neutral[100]};
`;

const CategoriesWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const CategoryItem = styled.div`
  display: inline-block;
  border-radius: 40px;
  padding: 2px 8px;

  color: ${color.neutral['100']};
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;

  background-color: ${color.neutral['85_alpha_20']};
`;

const Body = styled.div`
  position: relative;
  display: grid;
  align-content: end;
  grid-template-columns: 1fr;
  gap: 12px;
  padding: 16px;
  height: 100%;
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
`;

const RatingSummary = styled.div`
  display: flex;
  align-items: center;
  justify-content: left;
`;

const DisplayRating = styled.div`
  margin-right: 4px;
  font-size: 18px;
  font-weight: 900;
  line-height: 1.5;
  color: ${color.neutral[100]};
`;

const DisplayRatingCount = styled.div`
  margin-left: 4px;
  font-size: 12px;
  line-height: 1.5;
  color: ${color.neutral[75]};
  ::before {
    content: '(';
  }
  ::after {
    content: ')';
  }
`;

export default LivestreamOverlayCta;
